import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // action categories — same colours as the approved Home quick actions (lib/actionTones.js)
        create: "rounded-lg bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-emerald-400",
        manage: "rounded-lg border border-sky-200 bg-sky-50 text-sky-900 hover:border-sky-400 hover:bg-sky-100 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-sky-400 [&_svg]:text-sky-600",
        ops: "rounded-lg border border-teal-200 bg-teal-50 text-teal-900 hover:border-teal-400 hover:bg-teal-100 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-teal-400 [&_svg]:text-teal-600",
        finance: "rounded-lg border border-amber-200 bg-amber-50 text-amber-900 hover:border-amber-400 hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-amber-400 [&_svg]:text-amber-600",
        comms: "rounded-lg border border-green-200 bg-green-50 text-green-900 hover:border-green-400 hover:bg-green-100 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-green-400 [&_svg]:text-green-600",
        dangerSoft: "rounded-lg border border-rose-200 bg-rose-50 text-rose-800 hover:border-rose-400 hover:bg-rose-100 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-rose-400 [&_svg]:text-rose-600",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
